/**
 * VehicleFiscalCard — Per-vehicle fiscal info (ex-TVS) + ZFE compliance
 *
 * Displays: fiscal scope badge, CO2 tax, pollutant tax, total ex-TVS,
 * 3-year projection bars, Crit'Air badge, ZFE zone compliance, data quality.
 * "Simuler un remplacement" button opens ReplacementSimulatorModal.
 */
import React, { useState, useEffect } from 'react';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { useFormat } from '../../../i18n/format.js';
import { Receipt, TrendingUp, Leaf, ShieldCheck, ShieldX, ShieldAlert, AlertTriangle, HelpCircle, Zap } from 'lucide-react';
import { tvsAPI } from '../../../api/index.js';

const SCOPE_CONFIG = {
  taxable: { label: 'Taxable', color: 'bg-red-100 text-red-800' },
  outside: { label: 'Hors champ', color: 'bg-gray-100 text-gray-600' },
  review: { label: 'À revoir', color: 'bg-amber-100 text-amber-800' },
  exempt: { label: 'Exonéré', color: 'bg-green-100 text-green-800' },
};

const BAREME_LABELS = { wltp: 'WLTP', nedc: 'NEDC', pa: 'Puissance admin.', unknown: 'Inconnu' };

const CRITAIR_CONFIG = {
  E: { label: "Crit'Air E", color: 'bg-green-500 text-white' },
  '1': { label: "Crit'Air 1", color: 'bg-purple-500 text-white' },
  '2': { label: "Crit'Air 2", color: 'bg-yellow-400 text-gray-900' },
  '3': { label: "Crit'Air 3", color: 'bg-orange-400 text-white' },
  '4': { label: "Crit'Air 4", color: 'bg-red-400 text-white' },
  '5': { label: "Crit'Air 5", color: 'bg-gray-500 text-white' },
  unknown: { label: "Crit'Air ?", color: 'bg-gray-300 text-gray-700' },
};

const ZFE_STATUS_ICON = {
  allowed: { Icon: ShieldCheck, color: 'text-green-600', label: 'Autorisé' },
  restricted: { Icon: ShieldAlert, color: 'text-amber-600', label: 'Restreint' },
  prohibited: { Icon: ShieldX, color: 'text-red-600', label: 'Interdit' },
  unknown: { Icon: HelpCircle, color: 'text-gray-400', label: 'Inconnu' },
};

function VehicleFiscalCard({ vehicle, onSimulate }) {
  // Devise de l'organisation du vehicule - un seul outil monetaire.
  const { money } = useOrgCurrency();
  const { fmt } = useFormat();
  const formatEur = (n) => (n != null ? money(n, vehicle, { decimals: 0 }) : '—');
  const [taxData, setTaxData] = useState(null);
  const [projection, setProjection] = useState(null);
  const [zfeData, setZfeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vehicle?.id) return;
    setLoading(true);
    Promise.all([
      tvsAPI.getVehicleTax(vehicle.id, 2026).catch(() => ({ data: null })),
      tvsAPI.getVehicleProjection(vehicle.id).catch(() => ({ data: null })),
      tvsAPI.getVehicleZFE(vehicle.id).catch(() => ({ data: null })),
    ]).then(([taxRes, projRes, zfeRes]) => {
      setTaxData(taxRes.data);
      setProjection(projRes.data);
      setZfeData(zfeRes.data);
    }).finally(() => setLoading(false));
  }, [vehicle?.id]);

  if (loading) {
    return <div className="text-center py-4 text-gray-500 text-sm">Calcul fiscal en cours...</div>;
  }

  if (!taxData) {
    return <div className="text-center py-4 text-gray-400 text-sm">Données fiscales indisponibles</div>;
  }

  const scope = SCOPE_CONFIG[taxData.fiscal_scope] || SCOPE_CONFIG.review;
  const bareme = BAREME_LABELS[taxData.bareme_type] || 'Inconnu';
  const critAir = CRITAIR_CONFIG[taxData.crit_air] || CRITAIR_CONFIG.unknown;

  // Projection max for bar scaling
  const projYears = projection ? [projection.y2025, projection.y2026, projection.y2027] : [];
  const projMax = Math.max(...projYears.map(y => y?.total_prorated || 0), 1);

  return (
    <div className="space-y-4">
      {/* Badges row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${scope.color}`}>{scope.label}</span>
        <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 font-medium">{bareme}</span>
        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${critAir.color}`}>{critAir.label}</span>
        {taxData.exempt && (
          <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 font-medium flex items-center gap-1">
            <Zap className="w-3 h-3" /> Exonéré ({taxData.exemption_reason === 'electric_only' ? '100% électrique' : '100% hydrogène'})
          </span>
        )}
      </div>

      {/* Tax amounts */}
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 uppercase">Taxe CO2</p>
          <p className={`text-lg font-bold ${taxData.co2_tax_net > 500 ? 'text-red-700' : taxData.co2_tax_net > 200 ? 'text-amber-700' : 'text-green-700'}`}>
            {formatEur(taxData.co2_tax_net)}
          </p>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 uppercase">Taxe polluants</p>
          <p className={`text-lg font-bold ${taxData.pollutant_tax_net > 200 ? 'text-red-700' : taxData.pollutant_tax_net > 0 ? 'text-amber-700' : 'text-green-700'}`}>
            {formatEur(taxData.pollutant_tax_net)}
          </p>
        </div>
        <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-600 uppercase font-semibold">Total ex-TVS</p>
          <p className={`text-xl font-bold ${taxData.total_prorated > 500 ? 'text-red-700' : taxData.total_prorated > 200 ? 'text-amber-700' : 'text-green-700'}`}>
            {formatEur(taxData.total_prorated)}
          </p>
          <p className="text-xs text-gray-500">{taxData.days_in_scope} jours / 2026</p>
        </div>
      </div>

      {/* 3-year projection */}
      {projection && (
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Projection 2025 → 2027
          </p>
          <div className="space-y-1.5">
            {[
              { year: 2025, data: projection.y2025 },
              { year: 2026, data: projection.y2026 },
              { year: 2027, data: projection.y2027 },
            ].map(({ year, data }) => {
              const total = data?.total_prorated || 0;
              const pct = projMax > 0 ? (total / projMax) * 100 : 0;
              return (
                <div key={year} className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-10 font-mono">{year}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${year === 2026 ? 'bg-blue-500' : 'bg-gray-400'}`}
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 w-16 text-right">{formatEur(total)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ZFE compliance — top 3 zones */}
      {zfeData?.zones && (
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2 flex items-center gap-1">
            <Leaf className="w-3 h-3" /> Conformité ZFE
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {zfeData.zones.filter(z => ['paris', 'lyon', 'marseille', 'strasbourg', 'toulouse', 'grenoble'].includes(z.zone_code)).map(z => {
              const cfg = ZFE_STATUS_ICON[z.status] || ZFE_STATUS_ICON.unknown;
              return (
                <div key={z.zone_code} className="flex items-center gap-1.5 p-2 bg-gray-50 rounded text-xs">
                  <cfg.Icon className={`w-4 h-4 ${cfg.color}`} />
                  <span className="text-gray-700 font-medium">{z.zone_name}</span>
                  <span className={`ml-auto ${cfg.color} font-medium`}>{cfg.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Data quality warnings */}
      {taxData.warnings?.length > 0 && (
        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="text-amber-800">
            {taxData.warnings.map((w, i) => <p key={i}>{w}</p>)}
          </div>
        </div>
      )}

      {/* Simulate button */}
      {onSimulate && (
        <div className="flex justify-end pt-2 border-t border-gray-100">
          <button
            onClick={() => onSimulate(vehicle)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
          >
            <Receipt className="w-4 h-4" /> Simuler un remplacement
          </button>
        </div>
      )}

      {/* Trace */}
      <p className="text-xs text-gray-400 text-right">
        Barème {taxData.meta?.tariff_version} • Calculé le {taxData.meta?.calculated_at ? fmt.date(taxData.meta.calculated_at) : '—'}
      </p>
    </div>
  );
}

export default VehicleFiscalCard;
